import React, { useState, useEffect } from "react"
import axios from "axios"
import { ToastContainer, toast } from "react-toastify"
import "react-toastify/dist/ReactToastify.css"
import Header from "../components/Header"
import "./BankAccountModification.css"

const getAuthToken = () =>
  document.cookie.replace(/(?:(?:^|.*;\s*)auth\s*=\s*([^;]*).*$)|^.*$/, "$1")

const BankAccountModificationPage = () => {
  const [bankData, setBankData] = useState(null)
  const [showEditModal, setShowEditModal] = useState(false)
  const [modalData, setModalData] = useState({})
  const [confirmBankAccount, setConfirmBankAccount] = useState("")
  const [codeSent, setCodeSent] = useState(false)
  const [verificationCode, setVerificationCode] = useState("")
  const [enteredCode, setEnteredCode] = useState("")
  const [ifscSuggestions, setIfscSuggestions] = useState([])
  const [isLoadingIfsc, setIsLoadingIfsc] = useState(false)

  useEffect(() => { fetchBankData() }, [])

  const fetchBankData = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/banking/get`,
        { headers: { auth: getAuthToken() } }
      )
      setBankData(response.data)
    } catch (error) {
      console.error("Error fetching bank account data:", error)
    }
  }

  const handleShowEditModal = () => {
    setModalData({
      bankAccountName: bankData?.bankAccountName || "",
      bankAccountNumber: bankData?.bankAccountNumber || "",
      bankName: bankData?.bankName || "",
      bankIFSC: bankData?.bankIFSC || "",
    })
    setConfirmBankAccount(bankData?.bankAccountNumber || "")
    setCodeSent(false)
    setEnteredCode("")
    setShowEditModal(true)
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setModalData((prev) => ({ ...prev, [name]: value }))
  }

  const handleSendCode = async () => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/banking/send-verification-code`,
        {},
        { headers: { auth: getAuthToken() } }
      )
      toast.success("Verification code sent to your email!")
      setVerificationCode(response.data.code)
    } catch (error) {
      toast.error("Error sending verification code.")
    }
    setCodeSent(true)
  }

  const handleIfscSearch = async (value) => {
    setModalData((prev) => ({ ...prev, bankIFSC: value }))
    if (value.length < 4) { setIfscSuggestions([]); return }
    setIsLoadingIfsc(true)
    try {
      const response = await axios.get(`https://ifsc.razorpay.com/${value}`)
      const bankDetails = response.data
      setIfscSuggestions([bankDetails])
      setModalData((prev) => ({ ...prev, bankName: bankDetails.BANK }))
    } catch {
      setIfscSuggestions([])
      setModalData((prev) => ({ ...prev, bankName: "" }))
    } finally {
      setIsLoadingIfsc(false)
    }
  }

  const handleIfscSelect = (suggestion) => {
    setModalData((prev) => ({ ...prev, bankIFSC: suggestion.IFSC, bankName: suggestion.BANK }))
    setIfscSuggestions([])
  }

  const handleSave = async () => {
    if (modalData.bankAccountNumber !== confirmBankAccount) {
      toast.error("Bank Account Numbers do not match!")
      return
    }
    try {
      await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/banking/update`,
        { ...modalData, code: enteredCode },
        { headers: { auth: getAuthToken() } }
      )
      setShowEditModal(false)
      toast.success("Bank Account Details Updated Successfully")
      fetchBankData()
    } catch {
      toast.error("Error updating bank account data.")
    }
  }

  const DetailRow = ({ label, value }) => (
    <div className="bk-detail-row">
      <span className="bk-detail-label">{label}</span>
      <span className="bk-detail-value">{value || "—"}</span>
    </div>
  )

  return (
    <div className="banking-page">
      <Header />
      <div className="bk-content">
        <div className="bk-page-header">
          <div>
            <h1 className="bk-page-title">Banking</h1>
            <p className="bk-page-sub">Manage your payout bank account details</p>
          </div>
          <button className="bk-btn-primary" onClick={handleShowEditModal}>
            Edit Details
          </button>
        </div>

        <div className="bk-card">
          <div className="bk-card-header">
            <div className="bk-card-icon">🏦</div>
            <div>
              <h2 className="bk-card-title">Bank Account Details</h2>
              <p className="bk-card-sub">Your linked payout account</p>
            </div>
            {bankData && (
              <span className="bk-badge-verified">Linked</span>
            )}
          </div>

          <div className="bk-details-grid">
            <DetailRow label="Account Holder Name" value={bankData?.bankAccountName} />
            <DetailRow label="Account Number" value={bankData?.bankAccountNumber ? `••••${bankData.bankAccountNumber.slice(-4)}` : null} />
            <DetailRow label="Bank Name" value={bankData?.bankName} />
            <DetailRow label="IFSC Code" value={bankData?.bankIFSC} />
          </div>

          {!bankData && (
            <div className="bk-empty">
              <div className="bk-empty-icon">🏦</div>
              <p>No bank account linked yet.</p>
              <button className="bk-btn-primary" onClick={handleShowEditModal}>Add Bank Account</button>
            </div>
          )}
        </div>

        <div className="bk-info-card">
          <div className="bk-info-icon">ℹ️</div>
          <div>
            <strong>Secure payouts</strong>
            <p>Payouts are processed after deducting an 8% platform fee. Changes to bank details require email verification.</p>
          </div>
        </div>
      </div>

      {showEditModal && (
        <div className="bk-modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="bk-modal" onClick={(e) => e.stopPropagation()}>
            <div className="bk-modal-header">
              <h2>Edit Bank Account</h2>
              <button className="bk-modal-close" onClick={() => setShowEditModal(false)}>✕</button>
            </div>
            <div className="bk-modal-body">
              <div className="bk-form-group">
                <label>Account Holder Name</label>
                <input
                  type="text"
                  name="bankAccountName"
                  value={modalData.bankAccountName}
                  onChange={handleChange}
                  placeholder="Full name as per bank records"
                />
              </div>
              <div className="bk-form-group">
                <label>Bank Account Number</label>
                <input
                  type="text"
                  name="bankAccountNumber"
                  value={modalData.bankAccountNumber}
                  onChange={handleChange}
                  placeholder="Enter account number"
                />
              </div>
              <div className="bk-form-group">
                <label>Confirm Account Number</label>
                <input
                  type="text"
                  value={confirmBankAccount}
                  onChange={(e) => setConfirmBankAccount(e.target.value)}
                  placeholder="Re-enter account number"
                />
              </div>
              <div className="bk-form-group">
                <label>IFSC Code</label>
                <input
                  type="text"
                  name="bankIFSC"
                  value={modalData.bankIFSC}
                  onChange={(e) => handleIfscSearch(e.target.value)}
                  placeholder="e.g. SBIN0001234"
                />
                {isLoadingIfsc && <span className="bk-ifsc-loading">Looking up IFSC...</span>}
                {ifscSuggestions.length > 0 && (
                  <div className="bk-ifsc-suggestions">
                    {ifscSuggestions.map((s, i) => (
                      <div key={i} className="bk-ifsc-item" onClick={() => handleIfscSelect(s)}>
                        <strong>{s.BANK}</strong> — {s.BRANCH} ({s.IFSC})
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="bk-form-group">
                <label>Bank Name</label>
                <input
                  type="text"
                  name="bankName"
                  value={modalData.bankName}
                  onChange={handleChange}
                  placeholder="Auto-filled from IFSC"
                  readOnly
                />
              </div>
              {codeSent && (
                <div className="bk-form-group">
                  <label>Verification Code</label>
                  <input
                    type="text"
                    value={enteredCode}
                    onChange={(e) => setEnteredCode(e.target.value)}
                    placeholder="Enter code from email"
                  />
                </div>
              )}
            </div>
            <div className="bk-modal-footer">
              <button className="bk-btn-ghost" onClick={() => setShowEditModal(false)}>Cancel</button>
              {!codeSent ? (
                <button className="bk-btn-primary" onClick={handleSendCode}>Send Verification Code</button>
              ) : (
                <button className="bk-btn-success" onClick={handleSave}>Update Details</button>
              )}
            </div>
          </div>
        </div>
      )}

      <ToastContainer position="top-right" autoClose={3000} theme="dark" />
    </div>
  )
}

export default BankAccountModificationPage
